import { readAccessDeviceCommandContext } from '@/api/core/access-device-command-context';
import {
  putAccessDeviceManagement,
  type AccessDeviceManagementWrite,
} from '@/api/core/access-device-management';
import {
  readAccessDeviceRegistry,
  type AccessDeviceRegistryItem,
  type AccessDeviceRegistryPage,
} from '@/api/core/access-device-registry';
import { readCurrentAccessDevice } from '@/api/core/access-workflow';
import { coreQueryKey } from '@/api/core/authorization';
import {
  readNativeAccessInventory,
  type NativeAccessInventory,
} from '@/api/core/native-access-inventory';
import { ApiError } from '@/api/http/errors';
import { UnsavedGuard } from '@/components/unsaved-guard';
import { Button, DirtyConfirmDialog } from '@mgrvtechgroup/admin-ds';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { commandOwner } from '../property/presentation/command-owner';
import { useCurrentCommand } from '../property/presentation/use-current-command';
import type { PropertyRuntime } from '../property/property-queries';

interface DoorAttempt {
  deviceId: string;
  body: AccessDeviceManagementWrite;
}
interface DoorCurrent {
  device: AccessDeviceRegistryItem | null;
  choices: AccessDeviceRegistryPage | null;
  slot: NativeAccessInventory['items'][number];
}
const conflict = () =>
  new ApiError({
    kind: 'http',
    service: 'core',
    status: 409,
    message: '현재 연결이 변경되었습니다.',
  });
const equalSlots = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');

export function SlotDoorEditor({
  runtime,
  propertyId,
  slotId,
  inventoryCursor,
  currentDeviceId,
  label,
  onClose,
}: {
  runtime: PropertyRuntime;
  propertyId: string;
  slotId: string;
  inventoryCursor: string | null;
  currentDeviceId: string | null;
  label: string;
  onClose: () => void;
}) {
  const scope = `slot-door:${propertyId}:${slotId}`;
  const [deviceId, setDeviceId] = useState(() => {
    const pending = commandOwner(runtime, scope).attempt?.body as DoorAttempt | undefined;
    return pending?.deviceId ?? currentDeviceId ?? '';
  });
  const [cursor, setCursor] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [closeConfirm, setCloseConfirm] = useState(false);
  const region = useRef<HTMLDivElement>(null);
  const confirmTrigger = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const cmd = useCurrentCommand<DoorCurrent, DoorAttempt>({
    runtime,
    scope,
    readKey: `${deviceId}:${cursor ?? ''}`,
    bits: 2,
    read: async () => {
      const inventory = await readNativeAccessInventory(
        runtime.client,
        propertyId,
        inventoryCursor,
      );
      const slot = inventory.items.find((item) => item.slot_id === slotId);
      if (!slot || !['CURRENT', 'NO_MAPPING'].includes(slot.state))
        throw new Error('현재 슬롯 연결을 확인하지 못했습니다. 연결 자료를 다시 조회해 주세요.');
      if (!deviceId) {
        if (slot.device) throw conflict();
        return {
          device: null,
          choices: await readAccessDeviceRegistry(runtime.client, {
            limit: 50,
            cursor: cursor ?? undefined,
          }),
          slot,
        };
      }
      const device = await readCurrentAccessDevice(runtime.client, deviceId);
      if (device.retired || device.connections.some((item) => item.resolution_state !== 'RESOLVED'))
        throw new Error('장비의 연결 공간을 모두 확인할 수 없어 변경할 수 없습니다.');
      const linked = device.slot_ids.includes(slotId);
      if ((linked && slot.device?.core_device_id !== deviceId) || (!linked && slot.device !== null))
        throw conflict();
      const context = await readAccessDeviceCommandContext(runtime.client, deviceId, propertyId);
      if (context.revision !== device.revision) throw conflict();
      const attempt = commandOwner(runtime, scope).attempt;
      const accepted = attempt?.accepted ? (attempt.body as DoorAttempt) : undefined;
      if (
        accepted &&
        (accepted.deviceId !== deviceId || !equalSlots(device.slot_ids, accepted.body.slot_ids))
      )
        throw new Error('저장된 전체 연결이 요청과 다릅니다. 현재 연결을 다시 확인해 주세요.');
      return { device, choices: null, slot };
    },
    validate: async (attempt?: DoorAttempt) => {
      if (attempt && !commandOwner(runtime, scope).attempt) {
        const current = await readCurrentAccessDevice(runtime.client, attempt.deviceId);
        const context = await readAccessDeviceCommandContext(
          runtime.client,
          attempt.deviceId,
          propertyId,
        );
        if (
          current.revision !== attempt.body.expected_revision ||
          context.revision !== current.revision
        )
          throw conflict();
      }
    },
    write: async (attempt: DoorAttempt) => {
      await putAccessDeviceManagement(runtime.client, propertyId, attempt.deviceId, attempt.body);
    },
    onActorChanged: () => {
      setConfirm(false);
    },
  });
  const currentValue = cmd.current;
  const device = currentValue?.device;
  const linked = device?.slot_ids.includes(slotId) ?? false;
  const locked = cmd.busy || !!cmd.attempt || !cmd.canWrite;
  const dirty = !!cmd.attempt || confirm || (!!device && !linked && !cmd.success);
  useEffect(() => {
    if (confirm) {
      region.current?.focus();
      region.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [confirm]);
  useEffect(() => {
    queueMicrotask(() => {
      setConfirm(false);
    });
  }, [currentValue]);
  useEffect(() => {
    if (cmd.success)
      void queryClient.invalidateQueries({
        queryKey: coreQueryKey(
          runtime.apiNamespace,
          runtime.session.getSnapshot().generation,
          'native-access-inventory',
          propertyId,
        ),
      });
  }, [cmd.success, queryClient, runtime, propertyId]);
  const save = () => {
    if (device?.gateway_id == null) return;
    void cmd.submit({
      deviceId: device.core_device_id,
      body: {
        command_id: crypto.randomUUID(),
        expected_revision: device.revision,
        device_id: device.device_id,
        gateway_id: device.gateway_id,
        device_type: device.device_type,
        device_name: device.device_name,
        gateway_name: device.gateway_name,
        battery: device.battery,
        is_active: device.is_active,
        retired: false,
        slot_ids: linked
          ? device.slot_ids.filter((id) => id !== slotId)
          : [...device.slot_ids, slotId],
      },
    });
  };
  return (
    <section aria-label="슬롯 문 연결" className="space-y-3 rounded-md border p-4">
      <UnsavedGuard when={dirty} includeSearch />
      <h3 className="font-semibold">{label} · 문 연결</h3>
      {cmd.error && <p role="alert">{cmd.error}</p>}
      {!cmd.current && !cmd.error && <p role="status">현재 문 연결을 조회하고 있습니다.</p>}
      {cmd.success && <p role="status">저장 후 장비의 전체 연결과 이 슬롯을 다시 확인했습니다.</p>}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={cmd.busy} onClick={() => void cmd.refresh()}>
          현재 문 연결 다시 조회
        </Button>
        <Button
          variant="outline"
          disabled={cmd.busy}
          onClick={() => {
            if (dirty) setCloseConfirm(true);
            else onClose();
          }}
        >
          문 연결 편집 닫기
        </Button>
      </div>
      {device && (
        <>
          <p>
            선택한 문: {device.device_name?.length ? device.device_name : device.device_id} · 현재{' '}
            {device.slot_ids.length}개 슬롯 연결
          </p>
          <ul className="space-y-1">
            {device.connections.map((item) => (
              <li key={item.slot_id}>
                {item.property_name ?? '지점명 없음'} · {item.room_no ?? '객실명 없음'} ·{' '}
                {item.slot_no ?? '슬롯명 없음'}
              </li>
            ))}
          </ul>
          <p className="caption text-muted-foreground">
            이 슬롯만 {linked ? '연결 해제' : '연결'}하며 다른 슬롯 연결과 장비 설정은 유지합니다.
            실제 장비 반영과 출입 허용은 확인하지 않습니다.
          </p>
          <Button
            ref={confirmTrigger}
            disabled={locked}
            onClick={() => {
              setConfirm(true);
            }}
          >
            {linked ? '문 연결 해제 내용 확인' : '문 연결 내용 확인'}
          </Button>
          {!linked && (
            <Button
              variant="outline"
              disabled={locked}
              onClick={() => {
                setDeviceId('');
                setConfirm(false);
              }}
            >
              다른 문 선택
            </Button>
          )}
        </>
      )}
      {cmd.current?.choices && (
        <div className="space-y-2">
          <p>연결할 문 선택 · 장비 목록의 현재 페이지</p>
          {!cmd.current.choices.items.length && <p>조회된 장비가 없습니다.</p>}
          {cmd.current.choices.items.map((item) => (
            <Button
              key={item.core_device_id}
              variant="outline"
              disabled={locked || item.retired}
              onClick={() => {
                setDeviceId(item.core_device_id);
                setConfirm(false);
              }}
            >
              {item.device_name?.length ? item.device_name : item.device_id}
              {item.retired ? ' · 퇴역' : ''}
            </Button>
          ))}
          {cmd.current.choices.next_cursor && (
            <Button
              variant="outline"
              disabled={locked}
              onClick={() => {
                setCursor(cmd.current?.choices?.next_cursor ?? null);
              }}
            >
              다음 장비 보기
            </Button>
          )}
          {cursor && (
            <Button
              variant="outline"
              disabled={locked}
              onClick={() => {
                setCursor(null);
              }}
            >
              첫 장비부터
            </Button>
          )}
        </div>
      )}
      {confirm && device && (
        <div
          role="region"
          aria-label="문 연결 변경 확인"
          tabIndex={-1}
          ref={region}
          className="space-y-2 rounded border p-3"
        >
          <p>
            {label} · {device.device_name?.length ? device.device_name : device.device_id}{' '}
            {linked ? '연결 해제' : '연결'}
          </p>
          <p>
            다른 {device.slot_ids.filter((id) => id !== slotId).length}개 슬롯 연결은 유지하고
            장비를 퇴역시키지 않습니다.
          </p>
          <Button disabled={locked} onClick={save}>
            확인하고 문 연결 저장
          </Button>
          <Button
            variant="outline"
            disabled={cmd.busy}
            onClick={() => {
              setConfirm(false);
              confirmTrigger.current?.focus();
            }}
          >
            계속 검토
          </Button>
        </div>
      )}
      {cmd.attempt && !cmd.attempt.accepted && (
        <Button disabled={cmd.busy} onClick={() => void cmd.submit()}>
          원래 문 연결 요청 결과 확인
        </Button>
      )}
      {cmd.attempt?.accepted && (
        <p role="status">
          저장 요청은 수락되었습니다. 현재 문 연결을 다시 조회해 결과를 확인해 주세요.
        </p>
      )}
      <DirtyConfirmDialog open={closeConfirm} onOpenChange={setCloseConfirm} onConfirm={onClose} />
    </section>
  );
}
